import { useEffect, useRef, useState } from 'react'
import { fetchMediaProxyBlob, fetchMediaFileBlob } from '../api'

/**
 * Превью медиа: локальный файл (mediaId) или через прокси CDN (cdnUrl).
 * Blob реально грузится только когда плитка попадает близко к viewport.
 */
export default function MediaThumbnail({
  modelId,
  cdnUrl,
  mediaId,
  kind = 'thumb',
  fallbackKind = 'preview',
  alt = '',
  className,
}) {
  const rootRef = useRef(null)
  const [blobUrl, setBlobUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    setBlobUrl(null)
    setFailed(false)
    setShouldLoad(false)
  }, [modelId, cdnUrl, mediaId, kind, fallbackKind])

  useEffect(() => {
    if (!modelId || (!mediaId && !cdnUrl)) return undefined
    const element = rootRef.current
    if (!element || typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true)
          observer.disconnect()
        }
      },
      { rootMargin: '700px 0px' },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [modelId, cdnUrl, mediaId, kind, fallbackKind])

  useEffect(() => {
    if (!shouldLoad || !modelId) return undefined
    setFailed(false)
    const fetchBlob = mediaId
      ? async () => {
          const primary = await fetchMediaFileBlob(modelId, mediaId, kind)
          if (primary || !fallbackKind || fallbackKind === kind) return primary
          return fetchMediaFileBlob(modelId, mediaId, fallbackKind)
        }
      : cdnUrl
        ? () => fetchMediaProxyBlob(modelId, cdnUrl)
        : null
    if (!fetchBlob) return undefined

    let revoked = false
    fetchBlob()
      .then((url) => {
        if (!revoked && url) {
          setBlobUrl(url)
        } else if (!revoked) {
          setFailed(true)
        }
      })
      .catch(() => {
        if (!revoked) setFailed(true)
      })

    return () => {
      revoked = true
      setBlobUrl(null)
    }
  }, [shouldLoad, modelId, cdnUrl, mediaId, kind, fallbackKind])

  const resolvedClassName = className || 'model-detail-media-placeholder'

  if (blobUrl) {
    return (
      <img
        src={blobUrl}
        alt={alt}
        className={className || 'model-detail-media-thumb'}
        loading="lazy"
      />
    )
  }

  return (
    <div ref={rootRef} className={resolvedClassName}>
      {failed ? 'Preview unavailable' : shouldLoad ? 'Loading...' : ''}
    </div>
  )
}
